import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_TAGS } from '@/shared/api';
import { isBetaFeatureEnabled, isRouteBetaFeatureEnabled } from '../lib/betaFeature';
import { fetchBetaFeatures, updateBetaFeature } from './api';
import type { BetaFeatureCode, BetaFeatureListResponse } from './types';

/** Query keys реестра бета-функций */
export const betaFeatureKeys = {
  all: [API_TAGS.BETA_FEATURES] as const,
  list: () => [...betaFeatureKeys.all, 'list'] as const,
};

/** Хук реестра бета-функций с их состоянием. */
export const useBetaFeatures = () =>
  useQuery({
    queryKey: betaFeatureKeys.list(),
    queryFn: fetchBetaFeatures,
  });

/** Состояние бета-функции вместе с признаком того, что реестр уже получен */
export type BetaFeatureState = {
  /** Функция включена; пока реестр не получен — выключена */
  isEnabled: boolean;
  /** Реестр получен (или попытка его получить завершилась) — состоянию функции уже можно верить */
  isResolved: boolean;
};

/**
 * Хук состояния одной бета-функции по её коду вместе с признаком готовности реестра.
 * Нужен там, где неотличимое от выключенной функции ожидание реестра привело бы к
 * необратимому решению — уходу с адреса или сбросу пользовательской настройки.
 */
export const useBetaFeatureState = (code: BetaFeatureCode | undefined): BetaFeatureState => {
  const { data, isFetched } = useQuery({
    queryKey: betaFeatureKeys.list(),
    queryFn: fetchBetaFeatures,
    select: (response: BetaFeatureListResponse) => isBetaFeatureEnabled(response.features, code),
  });

  return { isEnabled: data ?? false, isResolved: isFetched };
};

/**
 * Хук состояния одной бета-функции по её коду. Читает тот же реестр, что и раздел
 * управления, поэтому лишнего запроса не делает. Когда функция считается выключенной —
 * задаёт {@link isBetaFeatureEnabled}.
 */
export const useIsBetaFeatureEnabled = (code: BetaFeatureCode): boolean => useBetaFeatureState(code).isEnabled;

/**
 * Хук доступности того, что закрыто бета-функцией лишь иногда: маршрута, пункта меню.
 * Незаданный код означает «функцией не закрыто» — доступно всегда.
 */
export const useIsRouteBetaFeatureEnabled = (code: BetaFeatureCode | undefined): boolean => {
  const { data } = useBetaFeatures();
  return isRouteBetaFeatureEnabled(data?.features, code);
};

/** Хук переключения бета-функции для всех пользователей. */
export const useToggleBetaFeature = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateBetaFeature,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: betaFeatureKeys.list() }),
  });
};
